import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Phone, ArrowRight, ShieldCheck } from 'lucide-react';

// Counter Animation Hook
const useCountUp = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const [hasAnimated, setHasAnimated] = useState(false);
  const countRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const startTime = Date.now();
          const timer = setInterval(() => {
            const now = Date.now();
            const progress = Math.min((now - startTime) / duration, 1);
            const easeOutQuad = progress * (2 - progress); // Easing function
            setCount(Math.floor(easeOutQuad * (end - start) + start));
            
            if (progress === 1) {
              clearInterval(timer);
            }
          }, 16); // ~60fps

          return () => clearInterval(timer);
        }
      },
      { threshold: 0.3 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => {
      if (countRef.current) {
        observer.unobserve(countRef.current);
      }
    };
  }, [end, duration, start, hasAnimated]);

  return [count, countRef];
};

const StatCard = ({ num, text, idx }) => {
  const numValue = parseInt(num.replace(/\D/g, ''));
  const [count, countRef] = useCountUp(numValue, 1500);
  const suffix = num.includes('+') ? '+' : '';

  return (
    <div 
      ref={countRef}
      className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-5 transform hover:scale-110 hover:bg-white/10 transition-all duration-300 animate-fade-in-up cursor-pointer group"
      style={{ animationDelay: `${idx * 100}ms` }}
    >
      <div className="text-3xl md:text-4xl font-bold text-white mb-2 group-hover:text-purple-300 transition-colors">
        {count}{suffix}
      </div>
      <div className="text-sm text-gray-300 group-hover:text-gray-100 transition-colors">{text}</div>
    </div>
  );
};

export const Hero = ({ onConsultationClick }) => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-28 pb-8">
      {/* Background with Gradient */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-slate-900 to-orange-900"></div>
        {/* Animated grid pattern */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '100px 100px'
        }}></div>
      </div>

      {/* Animated Elements */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Content */}
      <div className="relative z-20 container mx-auto px-6 lg:px-12 text-center">
        <div className="max-w-5xl mx-auto">
          <div className="inline-block mb-8 px-6 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full">
            <span className="text-orange-400 font-semibold text-sm tracking-wide flex items-center gap-2">
              <span className="text-2xl">🏆</span>
              BANGALORE&apos;S TOP VIDEO &amp; FILM EDITING INSTITUTE
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-tight">
            Become a Professional
            <span className="block bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent mt-2">
              Video/Film Editor
            </span>
          </h1>
          
          {/* Software Logos. Single row on desktop. Mobile: 4 on top row, 3 on bottom row. */}
          <div className="flex flex-col items-center gap-6 mb-8 max-w-5xl mx-auto">
            {(() => {
              const softwareList = [
                { name: 'Adobe Premiere Pro', logo: 'https://upload.wikimedia.org/wikipedia/commons/4/40/Adobe_Premiere_Pro_CC_icon.svg' },
                { name: 'Adobe After Effects', logo: 'https://upload.wikimedia.org/wikipedia/commons/c/cb/Adobe_After_Effects_CC_icon.svg' },
                { name: 'Avid Media Composer', logo: 'https://customer-assets.emergentagent.com/job_mfedit-academy/artifacts/2kqgfb0b_Avid_idOtDRQU_w_1.png' },
                { name: 'DaVinci Resolve', logo: 'https://upload.wikimedia.org/wikipedia/commons/9/90/DaVinci_Resolve_17_logo.svg' },
                { name: 'Final Cut Pro', logo: 'https://upload.wikimedia.org/wikipedia/en/9/9f/2015_Final_Cut_Pro_Logo.png' },
                { name: 'Adobe Photoshop', logo: 'https://upload.wikimedia.org/wikipedia/commons/a/af/Adobe_Photoshop_CC_icon.svg' },
                { name: 'Gen AI Tools', logo: 'https://customer-assets.emergentagent.com/job_50813185-e1e2-4269-80fb-d043d7ec4164/artifacts/u7coynis_Screenshot_2026-06-07_180819-removebg-preview.png' },
              ];

              const renderLogo = (software, idx) => (
                <div
                  key={idx}
                  className="group cursor-pointer relative flex items-center justify-center"
                >
                  <div className="relative p-1">
                    <img
                      src={software.logo}
                      alt={software.name}
                      className="w-10 h-10 sm:w-12 sm:h-12 md:w-12 md:h-12 lg:w-14 lg:h-14 object-contain drop-shadow-2xl transition-all duration-500 ease-out group-hover:scale-125 group-hover:drop-shadow-[0_0_20px_rgba(168,85,247,0.8)] group-hover:-translate-y-2 group-hover:brightness-110"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/100/9333ea/ffffff?text=' + software.name.charAt(0);
                      }}
                    />
                  </div>
                  {/* Tooltip */}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-3 opacity-0 scale-90 invisible group-hover:opacity-100 group-hover:scale-100 group-hover:visible transition-all duration-300 pointer-events-none z-50 whitespace-nowrap">
                    <div className="bg-white/95 backdrop-blur-sm text-slate-900 text-xs font-semibold px-3 py-1.5 rounded-lg shadow-2xl">
                      {software.name}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-[-1px]">
                        <div className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-b-[5px] border-b-white/95"></div>
                      </div>
                    </div>
                  </div>
                </div>
              );

              return (
                <>
                  {/* Desktop: single row, all 7 logos */}
                  <div className="hidden md:flex md:flex-nowrap items-center justify-center gap-5 lg:gap-7 w-full max-w-3xl mx-auto px-2">
                    {softwareList.map((s, i) => renderLogo(s, i))}
                  </div>

                  {/* Mobile: 4 on top row, 3 centered on bottom row */}
                  <div className="flex md:hidden flex-col items-center gap-4 w-full">
                    <div className="grid grid-cols-4 gap-4 w-full max-w-xs mx-auto place-items-center">
                      {softwareList.slice(0, 4).map((s, i) => renderLogo(s, i))}
                    </div>
                    <div className="grid grid-cols-3 gap-4 w-full max-w-[14rem] mx-auto place-items-center">
                      {softwareList.slice(4, 7).map((s, i) => renderLogo(s, i + 4))}
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Tagline */}
            <p className="text-base md:text-lg lg:text-xl text-white/90 leading-relaxed max-w-3xl text-center">
              Learn video/film editing in Bangalore from <span className="font-semibold text-white">award-winning trainers & experts!</span>
            </p>
          </div>

          {/* Feature Badges */}
          <div className="flex flex-wrap justify-center gap-3 mb-12 max-w-4xl mx-auto">
            {[
              '1-on-1 Coaching',
              'Offline Classes',
              'Apple Mac Workstations',
              'Course Certificate'
            ].map((feature, idx) => (
              <span
                key={idx}
                className="px-5 py-2.5 backdrop-blur-md border rounded-full text-sm font-semibold transition-all duration-300 bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                {feature}
              </span>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
            <Button 
              size="lg" 
              onClick={onConsultationClick}
              className="group relative bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-10 py-7 text-lg rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300 border-0 overflow-hidden"
              style={{
                boxShadow: '0 0 20px rgba(168, 85, 247, 0.5), 0 0 40px rgba(236, 72, 153, 0.3)'
              }}
            >
              {/* Neon glow animation */}
              <span className="absolute inset-0 rounded-full animate-neon-glow"></span>
              <span className="relative z-10 flex items-center">
                Enquire Now
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </Button>
            
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => {
                const element = document.getElementById('seminar');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-white/10 backdrop-blur-md border-2 border-white/30 text-white px-10 py-7 text-lg rounded-full hover:bg-white/20 transform hover:scale-105 transition-all duration-300"
            >
              <ArrowRight className="mr-2 h-5 w-5" />
              Join Seminar
            </Button>
          </div>

          {/* Placement Assistance Guarantee — softened, inline above stats */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-500/10 backdrop-blur-md border border-emerald-500/30">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              <span className="text-emerald-200 text-sm md:text-base font-medium">
                100% Placement Assistance Guarantee<sup className="ml-0.5 text-amber-300">*</sup>
              </span>
            </div>
          </div>

          {/* Stats with Counter Animation */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 max-w-4xl mx-auto mb-3">
            {[
              { num: '750+', text: 'Students Trained' },
              { num: '500+', text: 'Students Placed' },
              { num: '125+', text: 'Hiring Partners' },
              { num: '25+', text: 'Years Experience' }
            ].map((stat, idx) => (
              <StatCard key={idx} num={stat.num} text={stat.text} idx={idx} />
            ))}
          </div>

          {/* Placement Assistance disclaimer */}
          <p className="text-center text-xs text-white/50 italic max-w-xl mx-auto mb-6 px-4">
            *Placement Assistance Guarantee is offered on select programs. Talk to the Admissions Office for details.
          </p>

          {/* Location Text */}
          <div className="text-center">
            <p className="text-white/70 text-sm md:text-base font-medium tracking-wide">
              Across our branches in Bangalore and Chennai
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
